import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Copy, CheckCircle2, KeyRound, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { toast } from 'sonner';

interface Props {
  onClose: () => void;
  /** Se passar um id de agente pending, modal abre direto exibindo o token salvo (recuperação). */
  recoverAgentId?: string;
}

interface GeneratedToken {
  agent_id: string;
  install_token: string;
  expires_at: string;
  install_command: string;
  seeded_routines?: number;
}

interface DownloadInfo {
  platform: string;
  file_name: string;
  available: boolean;
  size_bytes?: number;
}

export function InstallTokenModal({ onClose, recoverAgentId }: Props) {
  const [label, setLabel] = useState('');
  const [token, setToken] = useState<GeneratedToken | null>(null);
  const [loading, setLoading] = useState(!!recoverAgentId);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [downloads, setDownloads] = useState<DownloadInfo[]>([]);
  const [downloadingPlatform, setDownloadingPlatform] = useState<string | null>(null);

  const apiUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5100');

  useEffect(() => {
    apiClient.agentEtl.listDownloads().then(r => setDownloads(r.data.downloads)).catch(() => {});
  }, []);

  // Modo de recuperação: ao montar, busca o token do agent pending
  useEffect(() => {
    if (!recoverAgentId) return;
    apiClient.agentEtl.getInstallToken(recoverAgentId)
      .then(r => setToken({ ...r.data, install_command: (r.data.install_command as string).replace('<API_URL>', apiUrl) }))
      .catch(err => {
        const code = err?.response?.data?.error;
        setRecoverError(
          code === 'token_consumed' ? 'Este agente já foi registrado.' :
          code === 'token_expired'  ? 'Token expirado. Cancele este agente e gere um novo.' :
          'Não foi possível recuperar o token.'
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoverAgentId]);

  async function downloadBinary(platform: string, fileName: string) {
    setDownloadingPlatform(platform);
    try {
      // Resolve a URL via API autenticada. Backend escolhe CDN público (R2) ou path local.
      const { data } = await apiClient.agentEtl.getDownloadUrl(platform);
      if (data.source === 'cdn') {
        // CDN público: abre em nova aba — browser baixa direto, sem auth
        const a = document.createElement('a');
        a.href = data.url;
        a.download = fileName;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('Download iniciado!');
      } else {
        // Fallback local: blob via API (mantém Authorization no fetch)
        const r = await apiClient.agentEtl.downloadBinary(platform);
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('Download iniciado!');
      }
    } catch {
      toast.error('Falha no download. O binário pode não estar publicado ainda.');
    } finally {
      setDownloadingPlatform(null);
    }
  }

  function platformLabel(p: string): string {
    return ({
      'win-x64': 'Windows (exe single-file)',
      'win-x64-msi': 'Windows (instalador MSI)',
      'linux-x64': 'Linux x64 (binário)',
      'linux-arm64': 'Linux ARM64 (binário)',
      'linux-deb': 'Linux (.deb)',
      'linux-rpm': 'Linux (.rpm)',
    } as Record<string, string>)[p] ?? p;
  }

  function formatBytes(n?: number): string {
    if (!n) return '';
    if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${n} B`;
  }

  async function generate() {
    setLoading(true);
    try {
      const { data } = await apiClient.agentEtl.generateInstallToken(label || undefined);
      // resolve api_url no install_command
      setToken({
        ...data,
        install_command: (data.install_command as string).replace('<API_URL>', apiUrl),
      });
    } catch {
      toast.error('Falha ao gerar token. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiado!');
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl">
          <Dialog.Title className="text-lg font-semibold flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Token de instalação
          </Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground mt-1">
            Gere um token de uso único (24h de validade) e cole no agente no servidor do cliente.
          </Dialog.Description>

          {loading ? (
            <div className="mt-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
              Carregando…
            </div>
          ) : recoverError ? (
            <div className="mt-5 space-y-4">
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                {recoverError}
              </div>
              <div className="flex justify-end">
                <Button onClick={onClose}>Fechar</Button>
              </div>
            </div>
          ) : !token ? (
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium">Identificação opcional</label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex: Loja Centro - Caixa 1"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Aparece como hostname inicial na lista até o agente reportar o real.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                <Button onClick={generate} disabled={loading}>
                  {loading ? 'Gerando…' : 'Gerar token'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2 text-sm text-emerald-800">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">Token gerado!</div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Expira em {new Date(token.expires_at).toLocaleString('pt-BR')}. Uso único.
                  </div>
                  {(token.seeded_routines ?? 0) > 0 && (
                    <div className="text-xs text-emerald-700 mt-1">
                      ✓ {token.seeded_routines} rotinas padrão da Visual já criadas — o agente começa a
                      sincronizar assim que você cadastrar o ERP.
                    </div>
                  )}
                </div>
              </div>

              <Section
                label="1. Token (copie e cole no agente)"
                value={token.install_token}
                isCopied={copied === 'token'}
                onCopy={() => copy(token.install_token, 'token')}
              />

              <Section
                label="2. Comando pronto (abra PowerShell como administrador na pasta do agente)"
                value={token.install_command}
                isCopied={copied === 'cmd'}
                onCopy={() => copy(token.install_command, 'cmd')}
                mono
              />
              <div className="text-xs text-zinc-500 -mt-2 px-1">
                Antes de colar: <code className="bg-zinc-100 px-1 rounded">cd 'C:\Program Files\VisualSoftware\SignageAgent'</code>
              </div>

              <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-600 space-y-2">
                <div className="font-medium">Passos no servidor do cliente:</div>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    <span>Baixar o agente:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {downloads.filter(d => d.available).length === 0 && (
                        <span className="text-zinc-400 italic">Nenhum binário publicado ainda.</span>
                      )}
                      {downloads.filter(d => d.available).map(d => (
                        <Button
                          key={d.platform}
                          size="sm"
                          variant="outline"
                          onClick={() => downloadBinary(d.platform, d.file_name)}
                          disabled={downloadingPlatform === d.platform}
                        >
                          {downloadingPlatform === d.platform
                            ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            : <Download className="w-3 h-3 mr-1" />}
                          {platformLabel(d.platform)}
                          {d.size_bytes && <span className="text-zinc-400 ml-1">({formatBytes(d.size_bytes)})</span>}
                        </Button>
                      ))}
                    </div>
                  </li>
                  <li>Executar como administrador (cria o serviço Windows / systemd).</li>
                  <li>Rodar o comando acima para registrar o agente na plataforma.</li>
                  <li>No painel do agente, clicar <strong>Cadastrar ERP</strong> e informar host, banco, usuário e senha. (As rotinas padrão já foram criadas — não precisa configurar nada no portal.)</li>
                  <li>Pronto: o agente começa a sincronizar sozinho no próximo ciclo (~1 min).</li>
                </ol>
              </div>

              <div className="flex justify-end">
                <Button onClick={onClose}>Concluído</Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Section({
  label, value, isCopied, onCopy, mono,
}: { label: string; value: string; isCopied: boolean; onCopy: () => void; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-medium text-zinc-600 mb-1">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className={`flex-1 px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200 break-all text-sm ${mono ? 'font-mono' : ''}`}>
          {value}
        </div>
        <Button variant="outline" size="sm" onClick={onCopy} className="shrink-0">
          {isCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
